import { Injectable } from '@angular/core'

/**
 * Timezone Service
 *
 * Centralized service for handling timezone conversions across the application.
 * Uses the browser's local timezone for display and converts to/from UTC for storage.
 *
 * Key Features:
 * - Formats UTC timestamps for display in browser local time
 * - Parses local datetime inputs back to UTC for storage
 */
@Injectable({
    providedIn: 'root'
})
export class TimezoneService {

    /**
     * Get the browser's local timezone
     */
    getLocalTimezone(): string {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    /**
     * Format a timestamp for display using the browser's local timezone
     * @param timestamp ISO string, Date object, or undefined
     * @param options Formatting options
     * @returns Formatted string or empty string if no timestamp
     */
    formatTimestamp(
        timestamp: string | Date | undefined | null,
        options: {
            dateStyle?: 'full' | 'long' | 'medium' | 'short';
            timeStyle?: 'full' | 'long' | 'medium' | 'short';
            includeDate?: boolean;
            includeTime?: boolean;
        } = { dateStyle: 'short', timeStyle: 'short' }
    ): string {
        if (!timestamp) return '';

        const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
        if (isNaN(date.getTime())) return '';

        try {
            const formatOptions: Intl.DateTimeFormatOptions = {};

            // Handle explicit include flags
            if (options.includeDate !== undefined || options.includeTime !== undefined) {
                if (options.includeDate !== false) {
                    formatOptions.year = 'numeric';
                    formatOptions.month = 'short';
                    formatOptions.day = 'numeric';
                }
                if (options.includeTime !== false) {
                    formatOptions.hour = 'numeric';
                    formatOptions.minute = '2-digit';
                    formatOptions.hour12 = true;
                }
            } else {
                // Use dateStyle/timeStyle
                if (options.dateStyle) formatOptions.dateStyle = options.dateStyle;
                if (options.timeStyle) formatOptions.timeStyle = options.timeStyle;
            }

            return new Intl.DateTimeFormat('en-US', formatOptions).format(date);
        } catch (e) {
            return date.toLocaleString();
        }
    }

    /**
     * Format a timestamp as date only (no time)
     * @param timestamp ISO string or Date object
     * @param style Date formatting style
     */
    formatDate(
        timestamp: string | Date | undefined | null,
        style: 'full' | 'long' | 'medium' | 'short' = 'short'
    ): string {
        return this.formatTimestamp(timestamp, { dateStyle: style, timeStyle: undefined });
    }

    /**
     * Format a timestamp as time only (no date)
     * @param timestamp ISO string or Date object
     */
    formatTime(
        timestamp: string | Date | undefined | null
    ): string {
        if (!timestamp) return '';

        const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
        if (isNaN(date.getTime())) return '';

        return new Intl.DateTimeFormat('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        }).format(date);
    }

    /**
     * Format for datetime-local input (YYYY-MM-DDTHH:mm)
     * Converts the UTC timestamp to local time for editing
     * @param timestamp ISO string or Date object
     */
    formatForDateTimeInput(timestamp: string | Date | undefined | null): string {
        if (!timestamp) return '';

        const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
        if (isNaN(date.getTime())) return '';

        // Use local time components for the input
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    /**
     * Convert a datetime-local input value to ISO string
     * The input value is in local time, convert to UTC for storage
     * @param dateTimeLocalValue Value from datetime-local input (YYYY-MM-DDTHH:mm)
     */
    parseFromDateTimeInput(dateTimeLocalValue: string): string | null {
        if (!dateTimeLocalValue) return null;

        try {
            // The datetime-local input is in local time
            // new Date() will parse it as local time and convert to UTC internally
            const date = new Date(dateTimeLocalValue);
            return isNaN(date.getTime()) ? null : date.toISOString();
        } catch (e) {
            console.warn('Failed to parse datetime input:', e);
            return null;
        }
    }

    /**
     * Get the timezone abbreviation (e.g., 'MST', 'EST', 'PST')
     * @param date Optional date for DST-aware abbreviation
     */
    getTimezoneAbbreviation(date?: Date): string {
        const d = date || new Date();

        try {
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZoneName: 'short'
            });

            const parts = formatter.formatToParts(d);
            return parts.find(p => p.type === 'timeZoneName')?.value || '';
        } catch (e) {
            return '';
        }
    }

    /**
     * Format a compact date (M/d format) in local timezone
     */
    formatCompactDate(timestamp: string | Date | undefined | null): string {
        if (!timestamp) return '';

        const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
        if (isNaN(date.getTime())) return '';

        return new Intl.DateTimeFormat('en-US', {
            month: 'numeric',
            day: 'numeric'
        }).format(date);
    }

    /**
     * Format a compact time (h:mm a format) in local timezone
     */
    formatCompactTime(timestamp: string | Date | undefined | null): string {
        return this.formatTime(timestamp);
    }
}
